import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Send,
  Download,
  Building2,
  User,
  Mail,
  Phone,
  Globe,
  CreditCard,
  Calendar,
  Clock,
  Package,
  FileText,
  CheckCircle,
  XCircle,
  Edit,
} from 'lucide-react';

/* ─── helpers ─────────────────────────────────────────────────────── */

const statusConfig = {
  draft:    { label: 'Bozza',     color: '#71717a', icon: Edit         },
  sent:     { label: 'Inviato',   color: '#3b82f6', icon: Mail         },
  accepted: { label: 'Accettato', color: '#10b981', icon: CheckCircle  },
  rejected: { label: 'Rifiutato', color: '#ef4444', icon: XCircle      },
  expired:  { label: 'Scaduto',   color: '#f59e0b', icon: Clock        },
};

const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  const cfg = statusConfig[status] || statusConfig.draft;
  const Icon = cfg.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
      padding: '0.3rem 0.85rem', borderRadius: '9999px',
      fontSize: '0.8rem', fontWeight: '600',
      background: `${cfg.color}20`, color: cfg.color,
    }}>
      <Icon size={13} />
      {t('admin.quotes.statusLabels.' + status, cfg.label)}
    </span>
  );
};

const fmt = (n) => `€${Number(n || 0).toFixed(2)}`;
const dateFmt = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '—';

/* ─── QuotePreviewModal ───────────────────────────────────────────── */

/**
 * QuotePreviewModal — readonly display of all quote details.
 *
 * Props:
 *   quote      — full quote object from the API
 *   onClose    — () => void
 *   onSend     — (quoteId) => void   (only shown for draft/sent)
 *   onDownload — (quoteId, quoteNumber) => void
 */
const QuotePreviewModal = ({ quote, onClose, onSend, onDownload }) => {
  const { t } = useTranslation();

  if (!quote) return null;

  const ci = quote.companyInfo || {};

  /* totals */
  const subtotal = (quote.items || []).reduce(
    (sum, item) => sum + (item.quantity || 0) * (item.pricePerUnit || 0), 0
  );
  const beforeTax = subtotal + (quote.urgentSurcharge || 0) + (quote.shippingCost || 0);
  const taxAmount = beforeTax * ((quote.taxRate || 22) / 100);
  const total     = beforeTax + taxAmount;

  const paymentLabels = {
    immediate: t('admin.quotes.preview.paymentImmediate'),
    net15:     t('admin.quotes.preview.paymentNet15'),
    net30:     t('admin.quotes.preview.paymentNet30'),
    net60:     t('admin.quotes.preview.paymentNet60'),
    deposit30: t('admin.quotes.preview.paymentDeposit30'),
    deposit50: t('admin.quotes.preview.paymentDeposit50'),
    custom:    quote.paymentTermsCustom || t('admin.quotes.preview.paymentCustom'),
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content quote-modal"
        style={{ maxWidth: '780px', maxHeight: '90vh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <h2 style={{ margin: 0 }}>{quote.quoteNumber}</h2>
            <StatusBadge status={quote.status} />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {(quote.status === 'draft' || quote.status === 'sent') && onSend && (
              <button
                onClick={() => onSend(quote._id)}
                className="admin-btn admin-btn-primary"
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.875rem' }}
              >
                <Send size={15} />
                {t('admin.quotes.actions.send', 'Invia')}
              </button>
            )}
            {onDownload && (
              <button
                onClick={() => onDownload(quote._id, quote.quoteNumber)}
                className="admin-btn"
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.875rem' }}
              >
                <Download size={15} />
                PDF
              </button>
            )}
            <button
              onClick={onClose}
              className="modal-close"
              aria-label={t('admin.common.close')}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

          {/* ── Dates row ── */}
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.875rem', color: '#64748b' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <Calendar size={14} />
              <strong>{t('admin.quotes.preview.issued')}</strong>&nbsp;{dateFmt(quote.createdAt)}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <Clock size={14} />
              <strong>{t('admin.quotes.preview.validUntil')}</strong>&nbsp;{dateFmt(quote.validUntil)}
            </span>
            {quote.projectReference && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <FileText size={14} />
                <strong>{t('admin.quotes.preview.ref')}</strong>&nbsp;{quote.projectReference}
              </span>
            )}
            {quote.language && (
              <span>
                <strong>{t('admin.quotes.preview.language')}</strong>&nbsp;{quote.language === 'it' ? '🇮🇹 Italiano' : '🇬🇧 English'}
              </span>
            )}
          </div>

          {/* ── Company + Customer side by side ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>

            {/* Company info */}
            <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '1rem' }}>
              <h4 style={{ margin: '0 0 0.75rem', color: '#10b981', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.875rem' }}>
                <Building2 size={15} /> {t('admin.quotes.companyInfo', 'Info Azienda')}
              </h4>
              <p style={infoLine}><strong>{ci.name || '—'}</strong></p>
              {ci.address && <p style={infoLine}>{ci.address}</p>}
              {(ci.postalCode || ci.city) && (
                <p style={infoLine}>{[ci.postalCode, ci.city].filter(Boolean).join(' ')}{ci.country ? `, ${ci.country}` : ''}</p>
              )}
              {ci.vatNumber && (
                <p style={infoLine}><span style={labelStyle}>P.IVA:</span> {ci.vatNumber}</p>
              )}
              {ci.iban && (
                <p style={infoLine}><span style={labelStyle}>IBAN:</span> {ci.iban}</p>
              )}
              {ci.email && (
                <p style={{ ...infoLine, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <Mail size={12} />{ci.email}
                </p>
              )}
              {ci.phone && (
                <p style={{ ...infoLine, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <Phone size={12} />{ci.phone}
                </p>
              )}
              {ci.website && (
                <p style={{ ...infoLine, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <Globe size={12} />{ci.website}
                </p>
              )}
            </div>

            {/* Customer info */}
            <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '1rem' }}>
              <h4 style={{ margin: '0 0 0.75rem', color: '#3b82f6', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.875rem' }}>
                <User size={15} /> {t('admin.quotes.customerInfo', 'Cliente')}
              </h4>
              <p style={infoLine}><strong>{quote.customerName || '—'}</strong></p>
              {quote.customerCompany && <p style={infoLine}>{quote.customerCompany}</p>}
              {quote.customerEmail && (
                <p style={{ ...infoLine, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <Mail size={12} />{quote.customerEmail}
                </p>
              )}
              {quote.customerPhone && (
                <p style={{ ...infoLine, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <Phone size={12} />{quote.customerPhone}
                </p>
              )}
              {quote.accountManager && (
                <p style={{ ...infoLine, marginTop: '0.75rem' }}>
                  <span style={labelStyle}>{t('admin.quotes.preview.accountManager')}:</span> {quote.accountManager}
                </p>
              )}
              {quote.accountManagerEmail && (
                <p style={{ ...infoLine, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <Mail size={12} />{quote.accountManagerEmail}
                </p>
              )}
            </div>
          </div>

          {/* ── Terms strip ── */}
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', padding: '0.75rem 1rem', background: '#f0fdf4', borderRadius: '8px', fontSize: '0.875rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <CreditCard size={14} />
              <strong>{t('admin.quotes.preview.payment')}</strong>&nbsp;{paymentLabels[quote.paymentTerms] || quote.paymentTerms || '—'}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <Package size={14} />
              <strong>{t('admin.quotes.preview.delivery')}</strong>&nbsp;{t('admin.quotes.preview.deliveryDays', { days: quote.deliveryTimeline || '—' })}
            </span>
            {quote.specialInstructions && (
              <span>
                <strong>{t('admin.quotes.preview.notes')}</strong>&nbsp;{quote.specialInstructions}
              </span>
            )}
          </div>

          {/* ── Items table ── */}
          <div>
            <h4 style={{ margin: '0 0 0.75rem', color: '#1e293b', fontSize: '0.9rem' }}>
              {t('admin.quotes.items', 'Articoli')}
            </h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  <th style={th}>#</th>
                  <th style={{ ...th, textAlign: 'left' }}>{t('admin.quotes.preview.colDescription')}</th>
                  <th style={{ ...th, textAlign: 'left' }}>{t('admin.quotes.preview.colMaterial')}</th>
                  <th style={th}>{t('admin.quotes.preview.colQty')}</th>
                  <th style={th}>{t('admin.quotes.preview.colWeight')}</th>
                  <th style={th}>{t('admin.quotes.preview.colUnitPrice')}</th>
                  <th style={th}>{t('admin.quotes.preview.colTotal')}</th>
                </tr>
              </thead>
              <tbody>
                {(quote.items || []).map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ ...td, textAlign: 'center', color: '#94a3b8' }}>{idx + 1}</td>
                    <td style={td}>{item.description || '—'}</td>
                    <td style={{ ...td, color: '#64748b' }}>{item.material || '—'}</td>
                    <td style={{ ...td, textAlign: 'center' }}>{item.quantity || 0}</td>
                    <td style={{ ...td, textAlign: 'center', color: '#64748b' }}>{item.weight ? `${item.weight}g` : '—'}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmt(item.pricePerUnit)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: '600', color: '#10b981' }}>
                      {fmt((item.quantity || 0) * (item.pricePerUnit || 0))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Totals ── */}
          <div style={{ marginLeft: 'auto', width: '260px', display: 'flex', flexDirection: 'column', gap: '0.4rem', fontSize: '0.875rem' }}>
            <div style={summaryRow}>
              <span>{t('admin.quotes.preview.subtotal')}</span>
              <span>{fmt(subtotal)}</span>
            </div>
            {(quote.urgentSurcharge || 0) > 0 && (
              <div style={summaryRow}>
                <span>{t('admin.quotes.preview.urgentSurcharge')}</span>
                <span>{fmt(quote.urgentSurcharge)}</span>
              </div>
            )}
            {(quote.shippingCost || 0) > 0 && (
              <div style={summaryRow}>
                <span>{t('admin.quotes.preview.shipping')}</span>
                <span>{fmt(quote.shippingCost)}</span>
              </div>
            )}
            <div style={summaryRow}>
              <span>{t('admin.quotes.preview.vat', { rate: quote.taxRate || 22 })}</span>
              <span>{fmt(taxAmount)}</span>
            </div>
            <div style={{ ...summaryRow, borderTop: '2px solid #10b981', paddingTop: '0.5rem', fontWeight: '700', fontSize: '1rem', color: '#10b981' }}>
              <span>{t('admin.quotes.preview.total')}</span>
              <span>{fmt(total)}</span>
            </div>
          </div>

          {/* ── Notes ── */}
          {quote.notes && (
            <div style={{ padding: '0.75rem 1rem', background: '#fffbeb', borderLeft: '3px solid #f59e0b', borderRadius: '0 6px 6px 0', fontSize: '0.875rem' }}>
              <strong style={{ color: '#92400e' }}>{t('admin.quotes.preview.notes')}</strong>
              <p style={{ margin: '0.25rem 0 0', color: '#78350f' }}>{quote.notes}</p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

/* ─── inline style objects (kept here, not in CSS, because they are
       specific to this modal's layout and don't need theming) ─────── */

const infoLine  = { margin: '0 0 0.25rem', fontSize: '0.85rem', color: '#374151' };
const labelStyle = { color: '#94a3b8', fontSize: '0.75rem' };
const th = { padding: '0.5rem 0.75rem', fontWeight: '600', color: '#475569', fontSize: '0.8rem', textAlign: 'center' };
const td = { padding: '0.5rem 0.75rem', color: '#1e293b', verticalAlign: 'top' };
const summaryRow = { display: 'flex', justifyContent: 'space-between', color: '#475569' };

export default QuotePreviewModal;
